import React from 'react';
import { Line } from 'react-chartjs-2';
import type { ChartOptions, Tick } from 'chart.js';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import TPOLabel from '../../../../components/TPOLabel/TPOLabel';
import ToolTip from '../../../../components/ToolTip/ToolTip';
import { numberFormatting, ratingFormatting, dateFormatting } from '../../../../common/helpers';
import { getColors } from '../../../../common/colorUtils';
import { DateArrayItem } from './AccountGraph.types';
import { InstitutionRating } from "../../../../networking/accounts/ratings/InstitutionRating";

interface AccountGraphProps {
    balances: InstitutionBalances[];
    accountRatings: InstitutionRating[];
    allRatings: InstitutionRating[];
    endDate: string;
}

export const VERIBANC_RATINGS_DROPDOWN_TESTID = 'VERIBANC_RATINGS_DROPDOWN_TESTID';

const createDateArray = (endingDate: string): DateArrayItem[] => {
    const endDate = new Date(endingDate);
    const oneYearBefore = new Date(endingDate);
    oneYearBefore.setFullYear(oneYearBefore.getFullYear() - 1);

    const dateArray: DateArrayItem[] = [];
    while (oneYearBefore <= endDate) {
        oneYearBefore.setDate(oneYearBefore.getDate() + 1);
        const localeDateString = new Date(oneYearBefore);
        dateArray.push({
            dateTime: localeDateString,
            value: null
        });
    }

    return dateArray;
};

// This is a presentation-only transform.
// It improves readability of the stacked chart by placing least-volatile institutions lower in the stack.
const sortInstitutionsForStack = (institutions: InstitutionBalances[]): InstitutionBalances[] => {
    if (!institutions || !Array.isArray(institutions) || institutions.length === 0) return [];

    // Calculate volatility (variance) for each institution to avoid recalculating during sort
    const withVolatility = institutions.map(inst => {
        const balances = inst.balances || [];
        let variance = 0;
        let average = 0;

        if (balances.length > 0) {
            const sum = balances.reduce((acc, curr) => acc + Number(curr.dailyBalanceTotal ?? 0), 0);
            average = sum / balances.length;

            const squaredDiffsSum = balances.reduce((acc, curr) => {
                const diff = Number(curr.dailyBalanceTotal ?? 0) - average;
                return acc + (diff * diff);
            }, 0);
            variance = squaredDiffsSum / balances.length;
        }

        return {
            original: inst,
            variance,
            average
        };
    });

    withVolatility.sort((a, b) => {
        // 1. Lowest volatility (variance) first, sits at the bottom of the stack
        if (a.variance !== b.variance) {
            return a.variance - b.variance;
        }

        // 2. Highest average balance next
        if (b.average !== a.average) {
            return b.average - a.average;
        }

        // 3. Alphabetical ascending name fallback
        const aName = a.original.institutionName || '';
        const bName = b.original.institutionName || '';
        return aName.localeCompare(bName);
    });

    // Extract exactly the original payload structure to return
    return withVolatility.map(item => item.original);
};

const AccountGraph = ({ balances, accountRatings, allRatings, endDate }: AccountGraphProps) => {
    const sortedBalances = React.useMemo(() => sortInstitutionsForStack(balances), [balances]);
    const options: ChartOptions<'line'> = {
        scales: {
            y: {
                beginAtZero: true,
                suggestedMax: 20,
                position: 'right',
                stacked: true,
                ticks: {
                    callback: (tickValue: string | number, index: number, ticks: Tick[]) =>
                        numberFormatting(+tickValue)
                }
            },
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    autoSkip: false,
                    callback: function (val, index) {
                        // Only display ticks that are the first of the month
                        const label = this.getLabelForValue(Number(val));
                        return new Date(label).getDate() === 1 ? this.getLabelForValue(Number(val)) : '';
                    }
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        return tooltipItem.dataset.label
                            ? tooltipItem.dataset.label + `: $${tooltipItem.formattedValue}`
                            : `$${tooltipItem.formattedValue}`;
                    }
                }
            }
        },
        elements: {
            point: {
                radius: 0
            }
        },
        interaction: {
            mode: 'index'
        }
    };

    const { t } = useTranslation();

    const getInstitutionData = (institutionBalance: InstitutionBalances['balances']) => {
        const dateArray = createDateArray(endDate);
        let institutionData: { dateTime: Date; value: number | null }[] = institutionBalance.map(
            (balance) => {
                return {
                    dateTime: new Date(balance.dateTime),
                    value: balance.dailyBalanceTotal
                };
            }
        );

        const dates = new Set(institutionData.map((d) => d.dateTime.toDateString()));

        institutionData = [
            ...institutionData,
            ...dateArray.filter((d) => {
                return !dates.has(d.dateTime.toDateString());
            })
        ];

        // We need to sort the array the chart displays the values in order of appearance without parsing the values
        institutionData.sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());

        return institutionData;
    };

    const generateData = (institutionBalance: InstitutionBalances['balances']) => {
        const institutionData = getInstitutionData(institutionBalance);

        const data = institutionData.map((balance) => {
            return balance.value;
        });

        return data;
    };

    const generateLabels = (institutionBalance: InstitutionBalances['balances']) => {
        const institutionData = getInstitutionData(institutionBalance);

        const labels = institutionData.map((balance) => {
            return balance.dateTime.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                timeZone: 'UTC'
            });
        });
        return labels;
    };

    const getRatingEffectiveDate = () => {
        if (allRatings && allRatings.length > 0) return dateFormatting(allRatings[0].effectiveDate);
        else return '';
    }

    const getRatingQuarterEnding = () => {
        if (allRatings && allRatings.length > 0) return dateFormatting(allRatings[0].quarterEnding);
        else return '';
    }

    const colors = getColors(sortedBalances.length);

    const datasets = sortedBalances
        ? sortedBalances.map((institutionBalances, index) => {
            const color = colors[index];
            return {
                label: institutionBalances.institutionName,
                data: generateData(institutionBalances.balances),
                fill: true,
                backgroundColor: color,
                borderColor: color
            };
        })
        : [];

    const labels = sortedBalances.length ? generateLabels(sortedBalances[0].balances) : [];

    const getRatingIndex = (currentBalances: InstitutionBalances[], rating: InstitutionRating) => {
        return currentBalances.findIndex(balance => balance.institutionName === rating.institutionName);
    }

    const data = {
        labels,
        datasets
    };

    return (
        <Box
            sx={{
                padding: 5,
                backgroundColor: '#ffffff'
            }}
        >
            <Stack spacing={3}>
                <Typography
                    align="right"
                    sx={{
                        fontSize: '18px',
                        color: 'text.primary',
                        fontWeight: 'bold'
                    }}
                >
                    {t('listOfAccounts.chartHeader')}
                </Typography>

                <Line data={data} options={options} />

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap'
                    }}
                >
                    {accountRatings && accountRatings.length > 0 &&
                        accountRatings.map((rating) => (
                            <div key={rating.institutionName}>
                                {
                                    getRatingIndex(sortedBalances, rating) !== -1 &&
                                    <TPOLabel text={rating.institutionName +
                                        ratingFormatting(rating.colorRating, rating.starRating)
                                    } color={colors[getRatingIndex(sortedBalances, rating)]}
                                    />
                                }
                            </div>

                        ))
                    }
                </Box>
                <TPOLabel text={t('listOfAccounts.ratings.legendExplanation',
                    {
                        effectiveDate: getRatingEffectiveDate(),
                        quarterEnding: getRatingQuarterEnding()
                    })}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-start'
                    }}
                >
                    <ToolTip
                        title={t('listOfAccounts.ratings.bankRatingToolTipTitle')}
                        content={t('listOfAccounts.ratings.bankRatingToolTipContent')}
                        label={t('common.explainBankRating')}
                    />
                    <Box sx={{ width: '20px' }} />
                    <ToolTip
                        title={t('listOfAccounts.chartToolTipTitle')}
                        content={t('listOfAccounts.chartToolTipContent')}
                        label={t('common.explainChart')}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

export default React.memo(AccountGraph);
